import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, 
  Tooltip, ResponsiveContainer, Legend 
} from "recharts";
import { Users, Eye, TrendingUp, Activity, RefreshCw } from "lucide-react";
import axios from "axios";
import { BACKEND_URL, API } from "@/lib/api";


export default function VisitorAnalytics() {
  const [stats, setStats] = useState(null);
  const [realtime, setRealtime] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(30);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("auth_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const [statsRes, realtimeRes] = await Promise.all([
        axios.get(`${API}/analytics/stats?days=${period}`, { headers }),
        axios.get(`${API}/analytics/realtime`, { headers })
      ]);
      
      setStats(statsRes.data);
      setRealtime(realtimeRes.data);
    } catch (error) {
      console.error("Error fetching analytics:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    
    // Refresh realtime stats every 30 seconds
    const interval = setInterval(async () => {
      try {
        const token = localStorage.getItem("auth_token");
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const res = await axios.get(`${API}/analytics/realtime`, { headers });
        setRealtime(res.data);
      } catch (e) {}
    }, 30000);
    
    return () => clearInterval(interval);
  }, [period]);

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Besucherstatistiken</h2>
        <div className="flex items-center gap-2">
          <select
            value={period}
            onChange={(e) => setPeriod(Number(e.target.value))}
            className="border rounded-lg px-3 py-2 text-sm"
          >
            <option value={7}>Letzte 7 Tage</option>
            <option value={30}>Letzte 30 Tage</option>
            <option value={90}>Letzte 90 Tage</option>
          </select>
          <Button onClick={fetchStats} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-1" />
            Aktualisieren
          </Button>
        </div>
      </div>

      {/* Realtime Card */}
      {realtime && (
        <Card className="bg-gradient-to-r from-[#001D24] to-[#001520] text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm">Aktive Besucher (letzten 5 Min.)</p>
                <p className="text-4xl font-bold">{realtime.active_visitors}</p>
              </div>
              <Activity className="h-12 w-12 text-green-200" />
            </div>
            {realtime.active_pages?.length > 0 && (
              <div className="mt-4 pt-4 border-t border-green-400/30">
                <p className="text-green-100 text-xs mb-2">Aktive Seiten:</p>
                <div className="flex flex-wrap gap-2">
                  {realtime.active_pages.map((page, i) => (
                    <span key={i} className="bg-green-400/20 px-2 py-1 rounded text-xs">
                      {page.page} ({page.count})
                    </span>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Summary Cards */}
      {stats?.summary && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Seitenaufrufe gesamt</p>
                  <p className="text-3xl font-bold">{stats.summary.total_page_views.toLocaleString()}</p>
                </div>
                <Eye className="h-10 w-10 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Eindeutige Besucher</p>
                  <p className="text-3xl font-bold">{stats.summary.total_unique_visitors.toLocaleString()}</p>
                </div>
                <Users className="h-10 w-10 text-[#001D24]" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Heute</p>
                  <p className="text-3xl font-bold">{stats.summary.today_views}</p>
                  <p className="text-xs text-muted-foreground">{stats.summary.today_unique} eindeutig</p>
                </div>
                <TrendingUp className="h-10 w-10 text-orange-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Ø Täglich</p>
                  <p className="text-3xl font-bold">{stats.summary.avg_daily_views}</p>
                  <p className="text-xs text-muted-foreground">Aufrufe/Tag</p>
                </div>
                <BarChart className="h-10 w-10 text-purple-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Charts */}
      {stats?.chart_data && stats.chart_data.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Traffic-Verlauf</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={stats.chart_data}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="date" 
                  tickFormatter={(date) => {
                    const d = new Date(date);
                    return `${d.getDate()}.${d.getMonth() + 1}`;
                  }}
                />
                <YAxis />
                <Tooltip 
                  labelFormatter={(date) => new Date(date).toLocaleDateString('de-DE')}
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="views" 
                  stroke="#3b82f6" 
                  name="Seitenaufrufe"
                  strokeWidth={2}
                />
                <Line 
                  type="monotone" 
                  dataKey="visitors" 
                  stroke="#10b981" 
                  name="Besucher"
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Top Pages */}
      {stats?.top_pages && stats.top_pages.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Beliebteste Seiten</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats.top_pages.map((page, i) => (
                <div key={i} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium">
                      {i + 1}
                    </span>
                    <span className="text-sm font-medium">{page.page}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-32 h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-primary rounded-full"
                        style={{ 
                          width: `${(page.views / stats.top_pages[0].views) * 100}%` 
                        }}
                      />
                    </div>
                    <span className="text-sm text-muted-foreground w-16 text-right">
                      {page.views.toLocaleString()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {(!stats?.chart_data || stats.chart_data.length === 0) && (
        <Card>
          <CardContent className="p-12 text-center">
            <Activity className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
            <h3 className="text-lg font-medium mb-2">Noch keine Daten</h3>
            <p className="text-muted-foreground text-sm">
              Die Besucherstatistiken werden automatisch erfasst, sobald Besucher auf Ihrer Seite sind.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
